import React, { useCallback, useRef, useEffect, useLayoutEffect, useState } from 'react';
import { components } from 'react-select';

export const MultiValueComponent = (props: any) => {
  return (
    <components.MultiValue key={props.data.value.toString()} {...props}>
      <span style={{
        fontSize: '14px',
        whiteSpace: 'nowrap',
      }}>{props.data.label}</span>
    </components.MultiValue>
  );
};

export const ValueContainerWithOverflowTip = (props: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const menuIsOpen: boolean = props.selectProps.menuIsOpen;

  // Separate MultiValue children from Input/Placeholder children
  const childrenArray = React.Children.toArray(props.children);
  const inputChild = childrenArray.find(
    (child: any) => child?.type?.displayName === 'Input' || child?.type?.name === 'Input'
  );
  const placeholderChild = childrenArray.find(
    (child: any) => child?.type?.displayName === 'Placeholder' || child?.type?.name === 'Placeholder'
  );
  const multiValueChildren = childrenArray.filter(
    (child: any) =>
      child?.type?.displayName !== 'Input' &&
      child?.type?.name !== 'Input' &&
      child?.type?.displayName !== 'Placeholder' &&
      child?.type?.name !== 'Placeholder'
  );

  const totalCount = multiValueChildren.length;
  const hiddenCount = visibleCount !== null ? totalCount - visibleCount : 0;

  // Measure how many chips fit using a hidden measuring container
  // This avoids rendering all chips in the visible container
  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container || totalCount === 0) {
      setVisibleCount(null);
      return;
    }

    // Create an offscreen measuring div that mirrors the container's width
    const measurer = document.createElement('div');
    measurer.style.cssText = 'position:absolute;visibility:hidden;height:0;overflow:hidden;display:flex;flex-wrap:nowrap;';
    measurer.style.width = container.offsetWidth + 'px';
    container.appendChild(measurer);

    // Grab the actual rendered chips from the container
    const chips = Array.from(
      container.querySelectorAll<HTMLElement>('.accSelector__multi-value')
    );

    const BADGE_RESERVE = 52;
    const available = container.offsetWidth - BADGE_RESERVE;

    let totalWidth = 0;
    let fits = 0;
    for (const chip of chips) {
      totalWidth += chip.offsetWidth + 4;
      if (totalWidth > available) break;
      fits++;
    }

    container.removeChild(measurer);

    // If everything fits, no need for the badge reserve check
    if (fits === totalCount) {
      // Re-check without badge reserve
      let fullWidth = 0;
      for (const chip of chips) {
        fullWidth += chip.offsetWidth + 4;
      }
      if (fullWidth <= container.offsetWidth) {
        setVisibleCount(null); // all fit, no badge needed
        return;
      }
    }

    setVisibleCount(Math.max(fits, 1)); // show at least 1
  }, [totalCount]);

  // Re-measure when chip count changes
  useLayoutEffect(() => {
    measure();
  }, [totalCount, measure]);

  // Recalculate on container resize
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  // Focus input when menu opens
  useEffect(() => {
    if (menuIsOpen) {
      containerRef.current?.querySelector('input')?.focus();
    }
  }, [menuIsOpen]);

  const displayedChildren = visibleCount !== null
    ? multiValueChildren.slice(0, visibleCount)
    : multiValueChildren;

  return (
    <div ref={containerRef} style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
      <components.ValueContainer {...props}>
        {totalCount === 0 && placeholderChild}
        {displayedChildren}
        {hiddenCount > 0 && (
          <span
            title={multiValueChildren.map((child: any) => child?.props?.data?.label).filter(Boolean).join(', ')}
            style={{ whiteSpace: 'nowrap', fontSize: '14px', color: 'var(--gray-500)', padding: '2px 4px', flexShrink: 0, cursor: 'default' }}
          >
            +{hiddenCount}
          </span>
        )}
        {inputChild}
      </components.ValueContainer>
    </div>
  );
};
